import { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, CameraOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function QrScanner({ onScan, onError }) {
  const [scanning, setScanning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const scannerRef = useRef(null);
  const containerRef = useRef(null);

  const startScanner = async () => {
    setStarting(true);
    setCameraError('');

    try {
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode("qr-reader");
      }

      await scannerRef.current.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 220, height: 220 },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          // Auto-stop after successful scan
          stopScanner();
          onScan(decodedText);
        },
        () => {} // ignore failures during scanning
      );
      setScanning(true);
    } catch (err) {
      const msg = err?.toString() || '';
      if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
        setCameraError('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (msg.includes('NotFoundError')) {
        setCameraError('No camera found on this device.');
      } else {
        setCameraError('Could not start camera. Try using the manual code input instead.');
      }
    } finally {
      setStarting(false);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2) { // SCANNING state
          await scannerRef.current.stop();
        }
      } catch { /* ignore */ }
    }
    setScanning(false);
  };

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (scannerRef.current) {
        try {
          scannerRef.current.stop();
        } catch { /* ignore */ }
      }
    };
  }, []);

  return (
    <div className="space-y-3">
      {!scanning ? (
        <Button
          onClick={startScanner}
          disabled={starting}
          variant="outline"
          className="rounded-full border-teal text-teal hover:bg-teal hover:text-white"
          data-testid="start-qr-scanner-btn"
        >
          {starting ? (
            <Loader2 size={16} className="animate-spin mr-2" />
          ) : (
            <Camera size={16} className="mr-2" />
          )}
          {starting ? 'Starting camera...' : 'Scan QR Code'}
        </Button>
      ) : (
        <Button
          onClick={stopScanner}
          variant="outline"
          className="rounded-full border-red-400 text-red-600 hover:bg-red-50"
          data-testid="stop-qr-scanner-btn"
        >
          <CameraOff size={16} className="mr-2" />
          Stop Scanner
        </Button>
      )}

      {cameraError && (
        <p className="text-sm text-red-600" data-testid="qr-camera-error">{cameraError}</p>
      )}

      <div
        id="qr-reader"
        ref={containerRef}
        className={`rounded-xl overflow-hidden transition-all ${scanning ? 'max-h-[300px] border-2 border-teal/30' : 'max-h-0'}`}
        style={{ width: scanning ? '100%' : 0, maxWidth: 320 }}
        data-testid="qr-reader-container"
      />
    </div>
  );
}
